import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';

interface DashboardProps {
  onTileClick: (tile: string, tab?: 'account' | 'users' | 'deployment') => void;
  userName?: string;
  tenantId: string;
}

const Dashboard: React.FC<DashboardProps> = ({ onTileClick, userName, tenantId }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const { instance, accounts } = useMsal();

  const getAccessToken = async () => {
    const account = accounts[0];
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  const handleSync = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setSyncing(true);
      const response = await fetch('/api/tenant/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        const data = await response.json();
        setSyncResult(data);
      } else {
        setSyncResult({ error: 'Failed to sync tenant. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to sync tenant:', error);
      setSyncResult({ error: 'An error occurred while syncing tenant.' });
    } finally {
      setSyncing(false);
    }
  };

  const faqData = [
    {
      question: "How do I deploy the add-in?",
      answer: "You will need to download the manifest.xml file, and upload it to your M365 environment in order for your signatures from Stampr to sync down and apply to your users. You can get the manifest and more detailed directions from the Settings > Deployment Instructions.",
      action: () => onTileClick('settings', 'deployment')
    },
    {
      question: "How often do signatures sync?",
      answer: "Signatures sync automatically every 6 hours in the Outlook add-in. If you need the latest signature in your add-in, you can open the add-in taskpane and click Sync Signatures."
    },
    {
      question: "How do I create a new email signature?",
      answer: "Click on the 'Signatures' tile on your dashboard, then click 'Create New Signature'. Use our editor to design your signature with dynamic placeholders like {{displayName}}, {{email}}, and {{jobTitle}}."
    },
    {
      question: "How do I assign signatures to users?",
      answer: "Navigate to the signature you want to assign, click 'Assignments', then select the Microsoft 365 groups. All members of the selected groups will be assigned the signature. Their signature will be updated either when the cache of their current signature expires (6 hours), or if they open the Outlook Addin and click Sync Signatures."
    },
    {
      question: "What are dynamic placeholders?",
      answer: "Dynamic placeholders like {{displayName}}, {{email}}, and {{companyName}} automatically populate with each user's information from Microsoft 365. This allows one signature template to work for your entire organization."
    },
    {
      question: "I need help... Who do I call?",
      answer: "You can reach our helpdesk at helpdesk@ryantechinc.com if you have any questions about how to set up Stampr!"
    },
  ];

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Welcome, {userName || 'User'}</h2>
        <button 
          className="btn-sync-dashboard"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? '🔄 Syncing...' : 'Sync Tenant'}
        </button>
      </div>
      <div className="dashboard-tiles">
        <div className="dashboard-tile" onClick={() => onTileClick('signatures')}>
          <div className="tile-icon">
            <img 
              src="https://stampraddin.blob.core.windows.net/images/stamp_logo.png" 
              alt="Signatures" 
              style={{ width: '48px', height: '48px' }}
            />
          </div>
          <h3>Signatures</h3>
          <p>Create and manage email signatures</p>
        </div>
        
        <div className="dashboard-tile" onClick={() => onTileClick('overview')}>
          <div className="tile-icon">📊</div>
          <h3>Overview</h3>
          <p>View metrics and usage</p>
        </div>
        
        <div className="dashboard-tile" onClick={() => onTileClick('settings')}>
          <div className="tile-icon">⚙️</div>
          <h3>Settings</h3>
          <p>Account info and user management</p>
        </div>
      </div>

      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqData.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <span>{faq.question}</span>
                <span className="faq-icon">{expandedFaq === index ? '−' : '+'}</span>
              </div>
              {expandedFaq === index && (
                <div className="faq-answer">
                  {faq.answer}
                  {faq.action && (
                    <button 
                      className="btn-faq-action" 
                      onClick={(e) => {
                        e.stopPropagation();
                        faq.action();
                      }}
                    >
                      Deployment Instructions
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {(syncing || syncResult) && (
        <div className="sync-modal-overlay" onClick={() => !syncing && setSyncResult(null)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            {syncing ? (
              <div className="sync-in-progress">
                <div className="sync-spinner"></div>
                <h3>Syncing Tenant</h3>
                <p>Syncing users and signature assignments from Microsoft 365...</p>
              </div>
            ) : syncResult?.error ? (
              <div className="sync-error">
                <div className="error-icon">⚠️</div>
                <h3>Sync Failed</h3>
                <p>{syncResult.error}</p>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            ) : (
              <div className="sync-success">
                <div className="success-icon">✓</div>
                <h3>Tenant sync complete!</h3>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="version-indicator">
        v1.0.8
      </div>
    </div>
  );
};

export default Dashboard;
